import { type CampaignCardData, CHANNEL_LABELS, formatInt, formatMXN, type ReachResult } from "@cafe/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Megaphone, Send } from "lucide-react";
import { useState } from "react";
import { api, unwrap } from "../../lib/api";
import { cn } from "../../lib/utils";
import { Badge, Button, Card, Modal, Spinner } from "../ui/primitives";
import { ReachFunnel } from "./ReachFunnel";

const STATUS: Record<string, { label: string; tone: "neutral" | "good" | "bad" | "warn" | "info" }> = {
  draft: { label: "Borrador", tone: "warn" },
  sending: { label: "Enviando…", tone: "info" },
  sent: { label: "Enviada", tone: "good" },
  failed: { label: "Falló", tone: "bad" },
};

interface Confirmation {
  confirmation_token: string;
  reach: ReachResult;
  reach_changed: boolean;
}

/**
 * Campaign preview + the ONLY way to send (ADR-3): the agent can create drafts, but sending
 * requires this human click → fresh reach check → explicit confirmation with a one-time token.
 */
export function CampaignCard({ initial }: { initial: CampaignCardData }) {
  const qc = useQueryClient();
  const live = useQuery({
    queryKey: ["campaign", initial.local_id],
    queryFn: async () => unwrap(await api.campaigns[":id"].$get({ param: { id: initial.local_id } })),
    initialData: initial as CampaignCardData & { sent_at?: string | null; error?: string | null },
    staleTime: 5_000,
  });
  const c = live.data ?? initial;
  const [confirm, setConfirm] = useState<Confirmation | null>(null);
  const [error, setError] = useState<string | null>(null);

  const prepare = useMutation({
    mutationFn: async () => unwrap(await api.campaigns[":id"].confirmation.$post({ param: { id: c.local_id } })),
    onSuccess: (data) => {
      setError(null);
      setConfirm(data as Confirmation);
    },
    onError: (e) => setError((e as Error).message),
  });
  const send = useMutation({
    mutationFn: async (token: string) => unwrap(await api.campaigns[":id"].send.$post({ param: { id: c.local_id }, json: { confirmation_token: token } })),
    onSuccess: () => {
      setConfirm(null);
      qc.invalidateQueries({ queryKey: ["campaign", c.local_id] });
      qc.invalidateQueries({ queryKey: ["campaigns"] });
    },
    onError: (e) => {
      setConfirm(null);
      setError((e as Error).message);
      qc.invalidateQueries({ queryKey: ["campaign", c.local_id] });
    },
  });

  const status = STATUS[c.status] ?? STATUS.draft!;
  const overLimit = c.message_length > c.message_limit;

  return (
    <Card className="overflow-hidden">
      <div className="flex items-start justify-between gap-2 border-b border-latte bg-latte/30 px-3 py-2">
        <div>
          <p className="flex items-center gap-1.5 text-sm font-semibold">
            <Megaphone className="size-4" /> {c.name}
          </p>
          <p className="text-xs text-roast/80">{c.objective}</p>
        </div>
        <Badge tone={status.tone}>{status.label}</Badge>
      </div>
      <div className="grid gap-3 p-3 sm:grid-cols-2">
        <div className="space-y-2 text-xs">
          <Field label="Audiencia">{c.audience_description}</Field>
          <Field label="Oferta">{c.offer_description}</Field>
          {c.cost_per_redemption !== null && (
            <Field label="Costo estimado por canje">
              {formatMXN(c.cost_per_redemption)}
              {c.cost_note && <span className="block text-[11px] text-roast/70">{c.cost_note}</span>}
            </Field>
          )}
          <ReachFunnel reach={c.reach} />
        </div>
        <div>
          <p className="mb-1 text-[11px] font-medium text-roast/80">Vista previa · {CHANNEL_LABELS[c.channel]}</p>
          <div className="rounded-2xl bg-[#e7f7e3] p-3 text-sm leading-relaxed whitespace-pre-wrap text-espresso shadow-inner">{c.message}</div>
          <p className={cn("mt-1 text-right text-[11px] tabular-nums", overLimit ? "text-red-700" : "text-roast/70")}>
            {c.message_length}/{c.message_limit} caracteres
          </p>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-latte px-3 py-2">
        {error && (
          <p role="alert" className="mr-auto flex items-center gap-1 text-xs text-red-700">
            <AlertTriangle className="size-3.5" /> {error}
          </p>
        )}
        {c.status === "sent" ? (
          <p className="flex items-center gap-1 text-xs text-emerald-800">
            <CheckCircle2 className="size-4" /> Enviada (simulado por la API de Luyoa).
          </p>
        ) : c.status === "draft" ? (
          <Button onClick={() => prepare.mutate()} disabled={prepare.isPending}>
            {prepare.isPending ? <Spinner /> : <Send className="size-4" />} Enviar campaña
          </Button>
        ) : null}
      </div>

      <Modal open={confirm !== null} onClose={() => setConfirm(null)} title="¿Enviar esta campaña?">
        {confirm && (
          <div className="space-y-3 text-sm">
            <p>
              Se enviará <strong>“{c.name}”</strong> por <strong>{CHANNEL_LABELS[c.channel]}</strong> a{" "}
              <strong>{formatInt(confirm.reach.reachable)} clientes</strong>. Esta acción no se puede deshacer.
            </p>
            {confirm.reach_changed && (
              <p className="flex items-start gap-1.5 rounded-lg bg-amber-50 p-2 text-xs text-amber-900">
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0" /> El alcance cambió desde que se creó el borrador (antes {formatInt(c.reach.reachable)}).
              </p>
            )}
            <ReachFunnel reach={confirm.reach} />
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="secondary" onClick={() => setConfirm(null)}>
                Cancelar
              </Button>
              <Button onClick={() => send.mutate(confirm.confirmation_token)} disabled={send.isPending}>
                {send.isPending ? <Spinner /> : <Send className="size-4" />} Sí, enviar
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[11px] text-roast/70">{label}</p>
      <p className="font-medium">{children}</p>
    </div>
  );
}
